// Single source of truth for the admin navigation surfaces (s44 nav fix; s103 grouping). BOTH
// the top nav (AppShell, via App.tsx) and the dashboard card grid (routes/admin/Dashboard.tsx)
// derive from this one list — so adding a feature is ONE line here and it appears in both, and
// the two surfaces can never drift out of step again (they had, badly: the card grid was a
// hardcoded showcase missing Packages/Vendors/Expenses/Settings). Per-entry flags let the two
// surfaces differ where they legitimately should.
//
// s103 (04 #93 slice (a), 02d ruling 2): the fifteen flat tabs collapse into FIVE parents —
// Work · Billing · Clients · Catalog · System — expressed HERE via `parent` and NEVER duplicated
// in AppShell (AppShell only renders whatever `children` it is handed). ROUTES DO NOT CHANGE:
// every admin route stays a flat /admin/<thing>, so deep links, linkTo targets, email paths and
// consume-once query contracts are untouched. Array order = the owner-ruled order WITHIN each
// parent (s103: Work is Time, Expenses, Mileage, Projects — the s58 order, overriding ruling 2's
// enumeration order); ADMIN_PARENT_ORDER below is the order OF the parents.

export type AdminParent = 'Work' | 'Billing' | 'Clients' | 'Catalog' | 'System';

/** The five parents in their locked nav/dashboard order (02d ruling 2). */
export const ADMIN_PARENT_ORDER: readonly AdminParent[] = ['Work', 'Billing', 'Clients', 'Catalog', 'System'];

export interface AdminSection {
  label: string;
  to: string;
  description: string;
  /** The nav/dashboard group this section belongs to (s103; 02d ruling 2). */
  parent: AdminParent;
  /** Show in the top nav (inside its parent's menu). Default true. */
  nav?: boolean;
  /** Show as a dashboard card. Default true. */
  card?: boolean;
  /** Placeholder for a not-yet-built feature — renders as a disabled card, never a menu item. */
  comingSoon?: boolean;
  /** 04 #125 WS-B (s193): a CONDITIONAL section, shown (nav AND card) only while the named flag from
   *  /api/me is true. 'collectsForOthers' = some brand carries a pass-through charge class (02g #125
   *  AMENDMENT (C), owner-ruled s193). The route itself stays reachable by URL and renders its own
   *  empty state, so hiding the entry never hides money. */
  when?: 'collectsForOthers';
}

/** The per-admin flags that decide the conditional sections (from /api/me via useAuth). */
export interface AdminSectionFlags {
  collectsForOthers: boolean;
}

/** True when a section is shown for these flags (unconditional sections always are). */
export function sectionShown(s: AdminSection, flags: AdminSectionFlags): boolean {
  return s.when === undefined || flags[s.when];
}

export const ADMIN_SECTIONS: AdminSection[] = [
  // Work (s103 order: Time, Expenses, Mileage, Projects — the s58 owner order)
  { label: 'Time', to: '/admin/time', description: 'Log billable time per client; retainer hours draw down automatically.', parent: 'Work' },
  { label: 'Expenses', to: '/admin/expenses', description: 'Recurring licenses and subscriptions, with seat utilization.', parent: 'Work' },
  { label: 'Mileage', to: '/admin/mileage', description: 'Log mileage per brand and client; billed rates and the IRS deduction apply.', parent: 'Work' },
  { label: 'Projects', to: '/admin/projects', description: 'Client projects that aggregate time, mileage, and expenses for progress billing.', parent: 'Work' },
  // Billing. 04 #93 slice (b) (owner, s213): the payments register became the MONEY register and the
  // label followed; the ROUTE did not (02b #93 rule 2). "AR aging" stays (owner-ruled s103).
  { label: 'Invoices', to: '/admin/invoices', description: 'Draft, issue, and settle client invoices; issuing stamps a permanent number.', parent: 'Billing' },
  { label: 'Money', to: '/admin/payments', description: 'Every payment, refund, credit note, client credit, write-off and early-pay discount, in one register.', parent: 'Billing' },
  { label: 'AR aging', to: '/admin/ar-aging', description: 'Outstanding client balances aged by how far past due, per client and in total.', parent: 'Billing' },
  // 04 #125 WS-B (s193): the fourth Billing child, CONDITIONAL on a pass-through class existing (02g #125
  // AMENDMENT (C)). Label "Remittances" by owner ruling s193 (V1), superseding (D)'s "Collected for others"
  // for the nav, the card and the page title; "Collected on behalf" stays the Settings/vendor-side wording.
  { label: 'Remittances', to: '/admin/remittances', description: 'Money collected on behalf of agencies and staff, what is still owed to each payee, and the remittances that paid it out.', parent: 'Billing', when: 'collectsForOthers' },
  // Clients — "Invite client" joins the nav for the FIRST time here (s103; it was card-only before) and keeps its card.
  { label: 'Clients', to: '/admin/clients', description: 'View and manage all clients across businesses.', parent: 'Clients' },
  { label: 'Invite client', to: '/admin/clients/invite', description: 'Send an invitation to a new business or consumer client.', parent: 'Clients' },
  { label: 'Documents', to: '/admin/documents', description: 'Store client, project, and internal documents with per-file client access controls.', parent: 'Clients' },
  // Catalog
  { label: 'Services', to: '/admin/services', description: 'Create, edit, and enable or disable catalog services.', parent: 'Catalog' },
  { label: 'Packages', to: '/admin/packages', description: 'Bundle services into packages clients can subscribe to.', parent: 'Catalog' },
  { label: 'Vendors', to: '/admin/vendors', description: 'Vendors and subcontractors for expenses and invoicing.', parent: 'Catalog' },
  // System — AI Usage stays a disabled dashboard card only (never a menu item), shown under System (s103).
  { label: 'Notifications', to: '/admin/notifications', description: 'Review system notifications and alerts.', parent: 'System' },
  { label: 'Settings', to: '/admin/settings', description: 'Branding, margins, glass, and platform defaults.', parent: 'System' },
  // 04 #160 WS-B (02i T4): the annual tax-category review. The dashboard ALSO raises a banner while it is
  // due (the trigger is the server's), but the page stays reachable here all year so a completed review
  // can be re-opened and a category corrected any day, not only while the banner shows. Under SYSTEM
  // (owner, s231 fix pass): a brand-level compliance task spanning the catalog, the clients' exemptions and
  // the brand's registrations — the catalog is only where one of its fixes lands.
  { label: 'Tax review', to: '/admin/tax-review', description: 'Each year, and whenever a tax rule changes: what becomes taxable and when, every service by tax category, exemptions and registrations to fix.', parent: 'System' },
  { label: 'AI Usage', to: '/admin/ai-usage', description: 'Coming soon (Phase 3).', parent: 'System', nav: false, comingSoon: true },
];

/** A child link inside a parent's menu. */
export interface AdminNavChild {
  label: string;
  to: string;
  adminOnly: true;
}

/** A top-level admin nav item: the flat "Admin" home link, or a parent carrying children. */
export interface AdminNavItem {
  label: string;
  to?: string;
  adminOnly: true;
  children?: AdminNavChild[];
}

/** Top-nav items for the admin surface (s103 shape): the flat "Admin" home link + the five
 *  parents, each carrying its non-coming-soon, nav-opted children. Shape matches AppShell's
 *  NavItem — AppShell renders a dropdown (desktop) / accordion (mobile) wherever `children`
 *  is present, and the grouping itself lives ONLY here. */
export function adminNavItems(flags: AdminSectionFlags = { collectsForOthers: false }): AdminNavItem[] {
  return [
    { label: 'Admin', to: '/admin', adminOnly: true },
    ...ADMIN_PARENT_ORDER.map((parent): AdminNavItem => ({
      label: parent,
      adminOnly: true,
      children: ADMIN_SECTIONS.filter((s) => s.parent === parent && s.nav !== false && !s.comingSoon && sectionShown(s, flags)).map(
        (s): AdminNavChild => ({ label: s.label, to: s.to, adminOnly: true }),
      ),
    })),
  ];
}
